import { useQuery } from '@tanstack/react-query';
import { useSession } from './authClient.js';
import { type SignalStateControls, useSignalState } from './signalState.js';
import type { Signal, SignalsFeed } from './signals.js';
import { useTRPC } from './trpc.js';
import { NO_STREAM } from './workflows.hooks.js';

// The badge and panel read the same cached feed on every page; a new run lands at most daily.
const FEED_STALE_MS = 60_000;
const FEED_POLL_MS = 5 * 60_000;

export interface SignalsQuery extends SignalStateControls {
  signals: Signal[];
  feed: SignalsFeed | null;
  isPending: boolean;
  // No feed at all; with a feed already shown, a failed refresh is `refreshFailed` instead.
  isError: boolean;
  refreshFailed: boolean;
  isFetching: boolean;
  retry: () => void;
}

export function useSignals(): SignalsQuery {
  const trpc = useTRPC();
  const { data: session } = useSession();
  // Until the session resolves there is no user to count, mark or fetch for.
  const userId = session?.user.id ?? null;
  const controls = useSignalState(userId);
  const query = useQuery(
    trpc.signals.feed.queryOptions(undefined, {
      enabled: userId !== null,
      staleTime: FEED_STALE_MS,
      refetchInterval: FEED_POLL_MS,
      trpc: NO_STREAM,
    }),
  );
  // A disabled query still returns what is cached, which may be someone else's feed.
  const signedIn = userId !== null;
  const feed = signedIn ? (query.data ?? null) : null;
  return {
    ...controls,
    signals: feed?.signals ?? [],
    feed,
    isPending: !signedIn || query.isPending,
    isError: signedIn && query.isError && feed === null,
    refreshFailed: signedIn && query.isError && feed !== null,
    isFetching: query.isFetching,
    retry: () => void query.refetch(),
  };
}
